import { TILE } from './constants';
import { unitDef, UNIT_DEFS } from '../content/units';
import { buildingDef, BUILDING_DEFS } from '../content/buildings';
import { isWalkable } from './path';
import type { Grid } from './path';
import { YARD_QUEUE, queueOfUnit } from '../content/production';
import type { Building, GameState, PlayerState, ProductionItem } from './types';

/*
 * ייצור ובנייה.
 *
 * שני תורים נפרדים לכל שחקן — מבנים ויחידות — בדיוק כמו ב-RA2: אפשר לבנות תחנת
 * כוח ולייצר טנק באותו זמן, אבל לא שני מבנים בבת אחת.
 *
 * **העלות מנוקזת בהדרגה ולא נגבית מראש.** זו לא קוסמטיקה: היא מה שהופך "להתחיל
 * לבנות" להחלטה הפיכה, ומה שגורם למחסור באשראי להאט ייצור במקום לחסום אותו.
 * ביטול מחזיר בדיוק את מה ששולם.
 *
 * מחסור בחשמל מאט את קצב הניקוז — מבנה שאין לו חשמל נבנה לאט, לא נעצר. גם זה
 * מ-RA2, וזה מה שהופך את תחנת הכוח להחלטה ולא לתיבת סימון.
 */

export const ProductionKind = {
    Building: 0,
    Unit: 1,
} as const;
export type ProductionKind = (typeof ProductionKind)[keyof typeof ProductionKind];

/*
 * `ProductionItem` מוגדר ב-types.ts יחד עם שאר המצב — שתי הגדרות לאותו דבר היו
 * מייצרות שני טיפוסים שאינם תואמים, וזה בדיוק מה שקרה בניסיון הראשון.
 */

/** כמה טיקים לוקח פריט בעלות 1000 כשיש חשמל מלא. */
const TICKS_PER_1000 = 200;

/** קצב מינימלי בתת-חשמל — 40% מהמלא. אף פעם לא אפס: מחסור מאט, לא חוסם. */
const LOW_POWER_RATE_PCT = 40;

export interface PowerState {
    readonly produced: number;
    readonly consumed: number;
}

export function powerOf(buildings: readonly Building[], player: number): PowerState {
    let produced = 0;
    let consumed = 0;
    for (const building of buildings) {
        if (building.owner !== player || building.hp <= 0) continue;
        const def = buildingDef(building.type);
        produced += def.powerProduced;
        consumed += def.powerConsumed;
    }
    return { produced, consumed };
}

/** אחוז קצב הייצור לפי מאזן החשמל. 100 = מלא. */
export function productionRatePct(power: PowerState): number {
    if (power.consumed <= power.produced) return 100;
    if (power.produced <= 0) return LOW_POWER_RATE_PCT;
    const ratio = Math.floor((power.produced * 100) / power.consumed);
    return Math.max(LOW_POWER_RATE_PCT, Math.min(100, ratio));
}

/**
 * התור שאליו שייך פריט: מבנה → המפקדה, יחידה → המבנה שמייצר אותה.
 *
 * **גזירה ולא שדה.** אילו התור היה נשמר בפקודה, שני צדדים ברשת היו יכולים
 * לחלוק עליו; כאן הוא נובע מהתוכן ולכן שווה בהכרח בשני הצדדים.
 */
export function queueIndexOf(item: { kind: number; type: number }): number {
    return item.kind === ProductionKind.Building ? YARD_QUEUE : queueOfUnit(item.type);
}

/** תור המבנים של שחקן. הוא היחיד שיש לו "הצב", ולכן יש לו שם. */
export function buildQueueOf(player: {
    readonly queues: readonly (readonly ProductionItem[])[];
}): readonly ProductionItem[] {
    return player.queues[YARD_QUEUE] ?? [];
}

export function costOf(item: { kind: number; type: number }): number {
    return item.kind === ProductionKind.Building
        ? buildingDef(item.type).cost
        : unitDef(item.type).cost;
}

/** תנאי-קדם: מה שצריך שיהיה בנוי כדי שאפשר יהיה להזמין את הפריט. */
export function prerequisitesMet(
    item: { kind: number; type: number },
    buildings: readonly Building[],
    player: number,
): boolean {
    const owned = new Set(
        buildings.filter((b) => b.owner === player && b.hp > 0).map((b) => buildingDef(b.type).id),
    );
    // מפקדה נדרשת לכל דבר — בלעדיה אין ממה לבנות.
    if (!owned.has('yard')) return false;

    /*
     * **עץ הטכנולוגיה הוא דאטה.** מבנה מצהיר על `requires`, ויחידה מצהירה מאיזה
     * מבנה היא יוצאת. אין כאן טבלת תלויות מרכזית לתחזק — מבנה חדש מביא איתו את
     * התנאים שלו, ויחידה חדשה רק אומרת מי מייצר אותה.
     */
    if (item.kind === ProductionKind.Unit) {
        return owned.has(unitDef(item.type).producedBy);
    }

    const def = buildingDef(item.type);
    /*
     * מבנה שאינו `buildable` נדחה כאן ולא רק מוסתר בממשק. המפקדה עולה 0, ולכן
     * ממשק שרק מסתיר אותה היה משאיר מפקדה חינם למי שישלח את הפקודה בכל דרך אחרת.
     */
    if (!def.buildable) return false;
    return def.requires.every((id) => owned.has(id));
}

export interface ProductionOutcome {
    readonly queues: readonly ProductionItem[];
    readonly spent: number;
}

/**
 * כמה מבני ייצור מזרזים את התור, לכל היותר.
 *
 * **בלי תקרה, מי שבנה עשרה מפעלים מייצר טנק בטיק אחד** — וזה הופך את המשחק
 * למרוץ בנייה ולא לקרב. ארבעה הם גם מה שאפשר להחזיק בפועל בכלכלה סבירה.
 */
const MAX_PRODUCTION_SPEEDUP = 4;

/**
 * מקדם תור אחד בטיק. מנקז אשראי לפריט הראשון בלבד — תור הוא תור, לא מקבילית.
 *
 * **`speedup` הוא מספר מבני הייצור מהסוג הרלוונטי.**
 *
 * זו הייתה חנק אמיתי שנמדד ב-`scripts/simBatch.ts`: הניקוז היה חמישה אשראי לטיק
 * בלי קשר לכמה מפעלים נבנו, ולכן שני יריבים הגיעו ל-20,000 אשראי שלא נוצל ולא
 * הצליחו להעמיד צבא. ב-RA2 מפעל שני **מזרז** את הייצור, וזה מה שהופך "לבנות עוד
 * מפעל" להחלטה כלכלית ולא לקישוט.
 */
export function advanceQueue(
    queue: readonly ProductionItem[],
    credits: number,
    ratePct: number,
    speedup = 1,
): ProductionOutcome {
    const head = queue[0];
    if (!head || head.ready) return { queues: queue, spent: 0 };

    const total = costOf(head);
    if (total <= 0) {
        return { queues: [{ ...head, paid: 0, ready: true }, ...queue.slice(1)], spent: 0 };
    }

    // הקצב נגזר מהעלות: פריט יקר נבנה לאט יותר, ובאותו יחס לכולם.
    const factor = Math.max(1, Math.min(MAX_PRODUCTION_SPEEDUP, speedup));
    const perTick = Math.max(1, Math.floor((1000 * ratePct * factor) / (TICKS_PER_1000 * 100)));
    const step = Math.min(perTick, total - head.paid, credits);
    if (step <= 0) return { queues: queue, spent: 0 };

    const paid = head.paid + step;
    const updated: ProductionItem = { ...head, paid, ready: paid >= total };
    return { queues: [updated, ...queue.slice(1)], spent: step };
}

/** ביטול הפריט בראש התור. מחזיר בדיוק את מה ששולם. */
export function cancelHead(queue: readonly ProductionItem[]): {
    queue: ProductionItem[];
    refund: number;
} {
    const head = queue[0];
    if (!head) return { queue: [...queue], refund: 0 };
    return { queue: queue.slice(1), refund: head.paid };
}

/**
 * האם אפשר להציב מבנה בפינה הזו: כל האריחים פנויים ועבירים, ובטווח בנייה
 * ממבנה קיים. בלי טווח הבנייה אפשר היה "לשתול" מבנים בבסיס האויב.
 */
export const BUILD_RADIUS = 8 * TILE;

export function canPlace(
    state: GameState,
    grid: Grid,
    player: number,
    type: number,
    tileX: number,
    tileY: number,
): boolean {
    const def = buildingDef(type);

    for (let dy = 0; dy < def.h; dy++) {
        for (let dx = 0; dx < def.w; dx++) {
            const x = tileX + dx;
            const y = tileY + dy;
            if (!isWalkable(grid, x, y)) return false;
            if (occupied(state, x, y)) return false;
        }
    }

    const cx = tileX * TILE + (def.w * TILE) / 2;
    const cy = tileY * TILE + (def.h * TILE) / 2;
    return state.buildings.some((building) => {
        if (building.owner !== player || building.hp <= 0) return false;
        const other = buildingDef(building.type);
        const ox = building.tileX * TILE + (other.w * TILE) / 2;
        const oy = building.tileY * TILE + (other.h * TILE) / 2;
        const dx = ox - cx;
        const dy = oy - cy;
        return dx * dx + dy * dy <= BUILD_RADIUS * BUILD_RADIUS;
    });
}

function occupied(state: GameState, tileX: number, tileY: number): boolean {
    return state.buildings.some((building) => {
        if (building.hp <= 0) return false;
        const def = buildingDef(building.type);
        return (
            tileX >= building.tileX &&
            tileX < building.tileX + def.w &&
            tileY >= building.tileY &&
            tileY < building.tileY + def.h
        );
    });
}

/** מה שאפשר להזמין — מוצג בסרגל הבנייה. נבנה מהתוכן, לא מרשימה שנייה. */
export function catalogue(): readonly { kind: number; type: number }[] {
    return [
        ...BUILDING_DEFS.map((def, index) => ({ def, index }))
            .filter(({ def }) => def.buildable)
            .map(({ index }) => ({ kind: ProductionKind.Building, type: index })),
        ...UNIT_DEFS.map((_def, index) => ({ kind: ProductionKind.Unit, type: index })),
    ];
}

/** מקום פנוי ליציאת יחידה שהושלמה, ליד המבנה שממנו היא יוצאת. */
export function spawnPointNear(
    state: GameState,
    grid: Grid,
    building: Building,
): { x: number; y: number } | null {
    const def = buildingDef(building.type);
    for (let ring = 1; ring <= 6; ring++) {
        for (let dy = -ring; dy <= def.h + ring - 1; dy++) {
            for (let dx = -ring; dx <= def.w + ring - 1; dx++) {
                const onEdge =
                    dx === -ring || dy === -ring || dx === def.w + ring - 1 || dy === def.h + ring - 1;
                if (!onEdge) continue;
                const x = building.tileX + dx;
                const y = building.tileY + dy;
                if (!isWalkable(grid, x, y)) continue;
                if (occupied(state, x, y)) continue;
                return { x: x * TILE + TILE / 2, y: y * TILE + TILE / 2 };
            }
        }
    }
    return null;
}

/**
 * המבנה שממנו יוצאת יחידה מסוימת — קסרקטין לחי"ר, מפעל לרק"ם.
 * יחידה יוצאת מהמקום שמייצר אותה, לא ממקום שרירותי בבסיס.
 */
/**
 * כמה מבנים חיים של השחקן מייצרים את הפריט שבראש התור.
 * למבנים — המפקדה; ליחידות — המבנה שמצוין ב-`producedBy`.
 */
export function producerCount(
    buildings: readonly Building[],
    player: number,
    head: { kind: number; type: number } | undefined,
): number {
    if (!head) return 1;
    const wanted =
        head.kind === ProductionKind.Building ? 'yard' : unitDef(head.type).producedBy;
    let count = 0;
    for (const building of buildings) {
        if (building.owner !== player || building.hp <= 0) continue;
        if (buildingDef(building.type).id === wanted) count++;
    }
    return Math.max(1, count);
}

/**
 * האם לשחקן יש **בכלל** מבנה חי שמייצר את הפריט הזה.
 *
 * נפרד מ-`producerCount` בכוונה: שם המינימום הוא 1, כי הוא מחזיר מכפיל זירוז
 * ומכפיל אפס היה עוצר כל ייצור. כאן השאלה אחרת לגמרי — האם יש למי לייצר.
 */
export function hasProducerFor(
    buildings: readonly Building[],
    player: number,
    head: { kind: number; type: number } | undefined,
): boolean {
    if (!head) return true;
    const wanted = head.kind === ProductionKind.Building ? 'yard' : unitDef(head.type).producedBy;
    return buildings.some(
        (building) =>
            building.owner === player &&
            building.hp > 0 &&
            buildingDef(building.type).id === wanted,
    );
}

export function productionBuilding(
    buildings: readonly Building[],
    player: PlayerState,
    unitType: number,
): Building | null {
    const source = unitDef(unitType).producedBy;
    for (const building of buildings) {
        if (building.owner !== player.index || building.hp <= 0) continue;
        if (buildingDef(building.type).id === source) return building;
    }
    return null;
}
